import {
  SnackbarContent,
  SnackbarProvider as NotistackProvider,
  CustomContentProps,
  closeSnackbar,
} from "notistack";
import { Box, styled, Theme, Typography, useMediaQuery } from "@mui/material";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BOTTOM_PANEL_INSET_VAR, CloseButton } from "@rentablo/components";
import CheckCircleIcon from "@icons/myicons/check-circle-filled.svg?react";
import InfoIcon from "@icons/myicons/info-filled.svg?react";
import ErrorIcon from "@icons/myicons/error-filled.svg?react";

const StyledIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  color: theme.palette.text.onTooltipPrimary,
  "& path": {
    stroke: theme.palette.background.tooltipPrimary,
  },
}));

const StyledCloseButton = styled(CloseButton)(({ theme }) => ({
  flexShrink: 0,
  marginLeft: "auto",
  color: theme.palette.text.onTooltipPrimary,
}));

const StyledSnackbarContent = styled(SnackbarContent, {
  shouldForwardProp: (prop) =>
    prop !== "autoHideDuration" &&
    prop !== "persist" &&
    prop !== "hideIconVariant" &&
    prop !== "anchorOrigin" &&
    prop !== "iconVariant" &&
    prop !== "showCloseButton",
})<{ showCloseButton?: boolean }>(({ theme, showCloseButton }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.tooltipPrimary,
  color: theme.palette.text.onTooltipPrimary,
  boxShadow: theme.shadows[1],
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2.5),
  padding: theme.spacing(2.5, 4),
  zIndex: 10000,

  // StyledEngineProvider injectFirst puts emotion ahead of notistack's own
  // stylesheet, so its flex-wrap: wrap beats any single-class selector here.
  "&&": {
    flexWrap: "nowrap",
  },

  "& a": {
    color: theme.palette.text.onTooltipPrimaryLink,
  },

  [theme.breakpoints.down("sm")]: {
    // Rise above the fixed bottom settings bar when a page has one.
    marginBottom: `calc(var(${BOTTOM_PANEL_INSET_VAR}, 0px) + ${theme.spacing(2)})`,
    maxWidth: "250px",
    alignItems: showCloseButton ? "center" : "flex-start",

    ...(showCloseButton && {
      "&&": {
        flexWrap: "wrap",
        rowGap: theme.spacing(1),
      },
      "& > .MuiTypography-root": {
        order: 1,
        flexBasis: "100%",
      },
    }),
  },
}));

const Snackbar = ({
  ref,
  ...props
}: CustomContentProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { t } = useTranslation();
  const { id, variant, message, showCloseButton, ...rest } = props;

  return (
    <StyledSnackbarContent
      data-testid={`snackbar-message-${variant}`}
      ref={ref}
      role="alert"
      showCloseButton={showCloseButton}
      {...rest}
    >
      {variant === "success" && (
        <StyledIcon>
          <CheckCircleIcon />
        </StyledIcon>
      )}
      {variant === "info" && (
        <StyledIcon>
          <InfoIcon />
        </StyledIcon>
      )}
      {variant === "error" && (
        <StyledIcon>
          <ErrorIcon />
        </StyledIcon>
      )}
      <Typography variant="body3">{message}</Typography>
      {showCloseButton && (
        <StyledCloseButton
          size="small"
          aria-label={t("common.close")}
          onClose={() => closeSnackbar(id)}
        />
      )}
    </StyledSnackbarContent>
  );
};

export const SnackbarProvider = ({ children }: { children?: ReactNode }) => {
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  return (
    <NotistackProvider
      maxSnack={3}
      Components={{
        success: Snackbar,
        info: Snackbar,
        error: Snackbar,
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: isLessSm ? "left" : "center",
      }}
    >
      {children}
    </NotistackProvider>
  );
};
